import * as THREE from 'three';
import { hudIcons, type HudIcon } from '../presentation/icons.js';
import { abilityIcon, classIcon } from '../presentation/content-icons.js';
import { gearIcon } from '../presentation/characters/gear-icons.js';
import { heroSummary } from '../presentation/party/hero-summary.js';
import { portraitUrl } from '../presentation/portraits.js';
import { pointTooltip } from '../presentation/tooltips.js';
import type { WorldHud } from '../presentation/hud/world-hud.js';
import type { AbilityView } from '../presentation/combat/ability-views.js';
import type { SelectionMode } from '../presentation/party/hero-selection.js';
import type { GameScene } from '../rendering/game-scene.js';
import { Hud3dScene, type HudHit, type MedallionState } from '../rendering/hud/hud-3d-scene.js';
import type { RadialNode } from '../rendering/hud/radial-menu.js';
import type { DockButton } from '../rendering/hud/hud-dock.js';
import type { HeroId } from '../../shared/game/world-types.js';
import { ADJACENT_FOES } from '../../shared/game/combat-distances.js';
import { actionSlots, attackStat } from '../../shared/game/combat-types.js';
import { GEOMETRY_EPSILON, surfaceDistance } from '../../shared/navigation/geometry.js';
import { FORMATION_SHAPES, type FormationShape } from '../../shared/game/formation.js';
import { formationGlyph } from '../presentation/formation-glyphs.js';
import { PING_CHOICES, type PingKind, type PingSubject } from '../../shared/network/pings.js';
import type { Point } from '../../shared/game/world-types.js';
import type { EmoteKind, PairEmoteKind } from '../../shared/game/emote-types.js';
import { PING_LOOKS } from '../presentation/ping-looks.js';
import { matches } from '../input/keymap.js';
import { retainShaderPrograms } from '../rendering/program-retention.js';
import type { ItemAction, ItemOrder } from '../presentation/inventory/inventory-model.js';

const GILT = 0xd8c08a;
/** Pointer travel, in pixels, beyond which a press on the chart's face drags it rather than clicks. */
const CLICK_SLOP = 6;
/** Milliseconds an Alt press is held before it opens the ping wheel instead of dropping a plain ping. */
const PING_HOLD = 280;
/** How soon a second click on a medallion must follow the first to count as a double click, in ms. */
const DOUBLE_CLICK = 400;
/** A weapon's glyph from what it deals: blades, points, everything else a plain strike. */
const WEAPON_ICONS: Record<string, HudIcon> = { slashing: 'gladius', piercing: 'dagger' };
/** The names of the party's formations, for the bar's tooltips. */
const FORMATION_NAMES: Record<FormationShape, string> = { line: 'Line', column: 'Column', wedge: 'Wedge', block: 'Block', ring: 'Ring' };
/** The bottom bar's tooltips. */
const DOCK_TEXTS: Record<string, string> = {
  behavior: 'Behavior · B', pack: 'Pack · I', rest: 'Rest or wait\nCatch Breath, Make Camp, Safe Rest or Wait, by party vote', stop: 'Stop\nCancel the selected heroes\' orders',
};

/** The radial menu's categories of powers, in ring order: a category of one is offered as that entry alone. */
const CATEGORIES = [['spells', 'Spells', 'spellbook'], ['powers', 'Powers', 'quarry'], ['tactics', 'Tactics', 'tactics']] as const;
const category = (view: AbilityView) => view.tactic ? 'tactics' : view.spell ? 'spells' : 'powers';
const SIZE = new THREE.Vector2();

/** The bronze reticle over the chart's face, the lit pointer over anything pressable, the world's own cursor elsewhere (the chart's frame included). */
const cursor = (hit: HudHit | null) => !hit || hit.kind === 'chart' && hit.id === 'frame' ? '' : hit.kind === 'chart' && hit.id === 'face' ? 'aim' : 'press';

/** What a menu entry does when chosen. */
type Entry =
  | { kind: 'ability' | 'autocast' | 'pair'; view: AbilityView }
  | { kind: 'attack'; heroId: HeroId }
  | { kind: 'behavior' | 'pack' | 'stop'; heroId: HeroId }
  | { kind: 'ping'; ping: PingKind }
  | { kind: 'emote'; emote: EmoteKind }
  | { kind: 'greet'; emote: PairEmoteKind; walker: HeroId }
  | { kind: 'goto' | 'halt' | 'follow' | 'point' }
  | { kind: 'item'; order: ItemOrder };

/** What an Alt press pointed at: a spot on the ground (in the world or on the chart) or a unit, and where its wheel stands on screen. */
interface PingTarget { subject: PingSubject; at: Point; targetId?: string; anchor: () => { x: number; y: number } | null }

/**
 * The game's HUD, drawn in 3D over a `WorldHud`: the party as lit medallions bearing each hero's face, and a radial menu that opens on whatever was right-clicked (or is under
 * the pointer when Q is pressed) and offers what the selected heroes can do to it. On an enemy: attacks and the powers that target
 * enemies. On a party member: the powers that target allies. On one of the selected heroes themselves: that hero's own and autocast
 * powers, with behavior, pack and Stop. With several heroes selected, the inner ring holds those who can act and each opens its
 * actions in the next ring. Along the bottom, a bronze rail carries the bar's buttons: the weapon (raising a column of the
 * weapons the selection shares, and Automatic), Behavior, Pack and Stop. The clock in the bottom right
 * corner is the pause button, with Rest (which also waits) on a short rail beside it. Every choice is a `WorldHud` call, so the rules and messages
 * are the HUD's own. Alt-click pings: on the ground, a unit or the chart, it drops a plain ping at once; held, it opens a ping wheel
 * of what fits that target, and letting go over a choice (or clicking one) sends it.
 */
export class WorldHud3d {
  private layer = new Hud3dScene();
  private views = new Map<string, AbilityView>();
  private entries = new Map<string, Entry>();
  /** The unit the menu is open on, if any. */
  private target: string | null = null;
  /** The weapon column is raised over the bar's weapon button. */
  private weapons = false;
  /** The formation column is raised over the bar's formation button. */
  private formations = false;
  private weaponTexts = new Map<string, string>();
  private hovered: HudHit | null = null;
  private pressed: HudHit | null = null;
  /** The spot of open ground the menu is open on: Move here, Stop and the ping choices. */
  private ground: PingTarget | null = null;
  private pointer: { clientX: number; clientY: number } | null = null;
  /** A left press made while the menu was open, away from it: it only closes the menu. */
  private swallow = false;
  /** A press on the chart's face, with any button: dragging it pans the chart, and only a press that stayed put is a click. */
  private chartDrag: { x: number; y: number; moved: boolean } | null = null;
  /** A medallion being dragged to a new place in the column, and where it would land among this player's heroes. */
  private reorder: { id: HeroId; x: number; y: number; moved: boolean; index: number } | null = null;
  private texts = new Map<string, string>();
  /** An Alt press waiting to become a plain ping (let go) or the wheel (held), and the wheel once open. */
  private pingPress: { target: PingTarget; timer: ReturnType<typeof setTimeout> } | null = null;
  private pingWheel: PingTarget | null = null;
  private last = performance.now();
  private detach: () => void;
  /**
   * What the layer reads from the page each frame, read again only when the page changes: a computed style forces a style recalculation
   * when anything else has dirtied the page that frame (plates, panels), and the top bar's buttons rarely change.
   */
  private zoom = 1; private inset = 0; private pageDirty = true;
  private corner: DockButton[] = []; private cornerDirty = true; private topbar: Element | null = null; private topbarAt = -Infinity;
  private pageWatch: MutationObserver; private topbarWatch: MutationObserver;
  private elements = new Map<string, HTMLElement>();
  private clockSpan = NaN;
  private lastMedallion: { id: string; at: number } | null = null;
  /**
   * The layer's own canvas over the page's panels (the combat log among them), seeing through to the world. While the inventory is open
   * it is lifted into the top layer over the dialog (`lift`, a manual popover) and draws the item menu alone, the rest going under the
   * dialog into the world's frame. Null where it could not be made: the layer then draws into the world's frame as a whole.
   */
  private gl: THREE.WebGLRenderer | null = null; private glFailed = false; private glWarmed = false; private painted = false;
  private lift = Object.assign(document.createElement('div'), { className: 'hud-lift', popover: 'manual' });
  /** An item's actions open round its slot in the inventory, and what a choice sends. */
  private item: { actions: ItemAction[]; anchor: { x: number; y: number }; choose: (order: ItemOrder) => void } | null = null;
  /** A press on the item menu: the click and context menu that follow it never reach the slot under it. */
  private itemPressed: HudHit | null = null; private itemSwallow = false;

  constructor(private hud: WorldHud, private host: HTMLElement, private scene: () => GameScene | null) {
    hud.onRefresh(() => this.sync());
    // Any order, from the menu, the keyboard, the world or a panel, closes the menu.
    hud.onOrder(() => { if (this.isOpen) this.close(); });
    const point = (event: MouseEvent) => { const box = host.getBoundingClientRect(); return { x: event.clientX - box.left, y: event.clientY - box.top }; };
    const pick = (event: MouseEvent) => this.live ? this.layer.pick(point(event)) : null;
    // A potion dragged from the pack onto a medallion is used on that hero, as the rules allow.
    const dropTarget = (event: DragEvent) => { const hit = pick(event); return (hit?.kind === 'medallion' || hit?.kind === 'stance') && hud.canDropOn(hit.id) ? hit : null; };
    // Capture on the stage, before the world sees the event: a press on the HUD never becomes a move order or a camera drag.
    const listeners: [string, (event: Event) => void][] = [
      ['pointermove', event => {
        const pointer = event as PointerEvent; this.pointer = { clientX: pointer.clientX, clientY: pointer.clientY };
        const drag = this.chartDrag;
        if (drag) {
          if (!drag.moved && Math.hypot(pointer.clientX - drag.x, pointer.clientY - drag.y) > CLICK_SLOP) { drag.moved = true; pointTooltip(null); host.dataset.hudCursor = 'grab'; }
          if (drag.moved) {
            const span = this.layer.chartSpan();
            this.scene()?.mapChart?.panBy((pointer.clientX - drag.x) / span, (pointer.clientY - drag.y) / span);
            drag.x = pointer.clientX; drag.y = pointer.clientY;
            event.stopImmediatePropagation(); return;
          }
        }
        const moving = this.reorder;
        if (moving) {
          if (!moving.moved && Math.hypot(pointer.clientX - moving.x, pointer.clientY - moving.y) > CLICK_SLOP) { moving.moved = true; moving.index = this.hud.selection.owned.indexOf(moving.id); pointTooltip(null); host.dataset.hudCursor = 'grab'; }
          if (moving.moved) { this.dragMedallion(moving, point(pointer).y); event.stopImmediatePropagation(); return; }
        }
        this.hovered = pick(pointer); this.layer.hover(this.hovered); host.dataset.hudCursor = cursor(this.hovered);
        pointTooltip(this.hovered ? this.tooltip(this.hovered) : null, pointer.clientX, pointer.clientY);
      }],
      ['pointerleave', () => { this.hovered = null; this.layer.hover(null); pointTooltip(null); delete host.dataset.hudCursor; }],
      ['pointerdown', event => {
        const pointer = event as PointerEvent, hit = pick(pointer);
        // Over the combat log, only a press on a piece drawn over it (or one closing the menu) is the HUD's; the rest is the log's.
        if (!hit && !host.contains(event.target as Node) && !(this.isOpen && pointer.button === 0)) { this.pressed = null; return; }
        // A press whose release never came (let go outside the window) is dropped.
        if (this.pingPress) { clearTimeout(this.pingPress.timer); this.pingPress = null; }
        if (pointer.button === 0 && pointer.altKey && this.live && !(this.pingWheel && hit?.kind === 'socket')) {
          const target = this.pingTarget(pointer, hit);
          if (target) {
            event.stopImmediatePropagation(); event.preventDefault();
            this.close(); this.pressed = null; this.chartDrag = null;
            this.pingPress = { target, timer: setTimeout(() => { if (this.pingPress?.target === target) this.openPingWheel(target); }, PING_HOLD) };
            return;
          }
        }
        this.pressed = hit; this.swallow = false;
        this.reorder = pointer.button === 0 && hit?.kind === 'medallion' && this.hud.selection.owned.includes(hit.id) ? { id: hit.id, x: pointer.clientX, y: pointer.clientY, moved: false, index: 0 } : null;
        this.chartDrag = hit?.kind === 'chart' && hit.id === 'face' ? { x: pointer.clientX, y: pointer.clientY, moved: false } : null;
        // A right press opens the menu at once on what it landed on: a unit, open ground, or the chart's face (its ping wheel).
        // It never drags the camera; the menu's own pieces still choose on release.
        const menuHit = !hit || hit.kind === 'chart' && hit.id === 'face';
        if (pointer.button === 2 && menuHit && this.live) {
          this.pressed = null; this.chartDrag = null; event.stopImmediatePropagation(); event.preventDefault();
          this.openAt(pointer, hit); return;
        }
        // A press on the HUD, or a left press anywhere while the menu is open, never reaches the world: no order, no selection.
        if (hit || this.isOpen && pointer.button === 0) { this.swallow = !hit; event.stopImmediatePropagation(); event.preventDefault(); }
      }],
      ['pointerup', event => {
        if (this.pingPress && (event as PointerEvent).button === 0) {
          event.stopImmediatePropagation(); event.preventDefault(); this.swallow = true;
          const { target, timer } = this.pingPress; clearTimeout(timer); this.pingPress = null;
          if (!this.pingWheel) { this.hud.ping(PING_CHOICES[target.subject][0], target.at, target.targetId); return; }
          // Let go over a choice sends it; anywhere else the wheel stays open for a click.
          const over = pick(event as PointerEvent), entry = over?.kind === 'socket' ? this.entries.get(over.id) : undefined;
          if (over && entry?.kind === 'ping') this.press(over, false);
          return;
        }
        const pointer = event as PointerEvent, hit = this.pressed, moved = this.reorder?.moved ? this.reorder : null, dragged = !!this.chartDrag?.moved || !!moved;
        this.pressed = null; this.chartDrag = null; this.reorder = null; this.layer.setDragged(null);
        if (hit) {
          event.stopImmediatePropagation(); event.preventDefault();
          if (moved) this.hud.moveHero(moved.id, moved.index);
          if (dragged) { host.dataset.hudCursor = cursor(pick(pointer)); return; }
          const now = pick(pointer);
          // Left and right both choose.
          if (now?.kind === hit.kind && now.id === hit.id) this.press(now, pointer.shiftKey);
          return;
        }
        if (this.swallow) { event.stopImmediatePropagation(); event.preventDefault(); this.close(); return; }
      }],
      ['click', event => { if (this.hovered || this.swallow) { event.stopImmediatePropagation(); this.swallow = false; } }],
      ['contextmenu', event => event.preventDefault()],
      ['dragover', event => { const hit = dropTarget(event as DragEvent); this.layer.hover(hit); if (hit) event.preventDefault(); }],
      ['dragleave', () => this.layer.hover(null)],
      ['drop', event => { const hit = dropTarget(event as DragEvent); this.layer.hover(null); if (hit && hud.dropOn(hit.id)) { event.preventDefault(); event.stopImmediatePropagation(); } }],
    ];
    // The layer is drawn over the combat log, so presses on it there are the layer's too.
    const under = [host, document.getElementById('combat-log')].filter(element => !!element);
    for (const [type, listener] of listeners) for (const element of under) element.addEventListener(type, listener, true);
    // Over the open inventory only the item menu is drawn, and only it is picked; a press elsewhere closes it without reaching anything.
    const dialog = document.getElementById('supplies-panel');
    const menuAt = (event: MouseEvent) => this.item ? this.layer.pick(point(event), true) : null;
    const itemListeners: [string, (event: Event) => void][] = [
      ['pointermove', event => {
        if (!this.item) return;
        const pointer = event as PointerEvent, was = this.hovered; this.hovered = menuAt(pointer); this.layer.hover(this.hovered);
        if (dialog) dialog.dataset.hudCursor = this.hovered ? 'press' : '';
        // Over a socket, the slot under it shows no tooltip of its own.
        if (this.hovered) { event.stopPropagation(); pointTooltip(this.tooltip(this.hovered), pointer.clientX, pointer.clientY); } else if (was) pointTooltip(null);
      }],
      ['pointerover', event => { if (this.item && this.hovered) event.stopPropagation(); }],
      ['pointerdown', event => {
        this.itemSwallow = false; this.itemPressed = null;
        if (!this.item) return;
        const hit = menuAt(event as PointerEvent); event.stopPropagation();
        if (hit) { this.itemPressed = hit; this.itemSwallow = true; event.preventDefault(); } else this.closeItem();
      }],
      ['pointerup', event => {
        const pressed = this.itemPressed; this.itemPressed = null;
        if (!pressed) return;
        event.stopPropagation(); event.preventDefault();
        const now = menuAt(event as PointerEvent);
        // Left and right both choose.
        if (now?.kind === pressed.kind && now.id === pressed.id) this.press(now, false);
      }],
      ['click', event => { if (this.itemSwallow) { event.stopPropagation(); event.preventDefault(); } }],
      ['contextmenu', event => { if (this.itemSwallow) { event.stopPropagation(); event.preventDefault(); } }],
      ['dragstart', event => { if (this.itemSwallow) event.preventDefault(); }],
      // Esc folds the menu before it can close the dialog.
      ['keydown', event => { if (this.item && (event as KeyboardEvent).key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.closeItem(); } }],
    ];
    for (const [type, listener] of itemListeners) dialog?.addEventListener(type, listener, true);
    hud.setItemMenu({ show: (actions, around, choose) => this.openItemMenu(actions, around, choose), close: () => this.closeItem() });
    // The wheel over the chart, or anywhere while it is unfolded, zooms the chart instead of the camera.
    const wheel = (event: WheelEvent) => {
      const chart = this.scene()?.mapChart, hit = pick(event);
      if (!chart || !(hit?.kind === 'chart' || chart.expanded && this.live)) return;
      event.preventDefault(); event.stopImmediatePropagation();
      const face = hit?.kind === 'chart' && hit.id === 'face';
      chart.zoomAt(face ? hit.u ?? .5 : .5, face ? hit.v ?? .5 : .5, -Math.sign(event.deltaY));
    };
    host.addEventListener('wheel', wheel, { capture: true, passive: false });
    // The interface size is set on the root's style, and the viewer's panel inset by a class on the page or one of the host's ancestors.
    this.pageWatch = new MutationObserver(() => { this.pageDirty = true; });
    for (let node: Element | null = host; node; node = node.parentElement) this.pageWatch.observe(node, { attributes: true, attributeFilter: ['class', 'style'] });
    this.topbarWatch = new MutationObserver(() => { this.cornerDirty = true; });
    this.detach = () => {
      for (const [type, listener] of listeners) for (const element of under) element.removeEventListener(type, listener, true);
      for (const [type, listener] of itemListeners) dialog?.removeEventListener(type, listener, true);
      host.removeEventListener('wheel', wheel, true); this.pageWatch.disconnect(); this.topbarWatch.disconnect();
    };
  }

  /** A dragged medallion follows the pointer and lands among this player's other heroes by its height; they glide apart round the gap. */
  private dragMedallion(drag: { id: HeroId; index: number }, y: number) {
    const others = this.layer.medallionSpots().filter(spot => spot.own && spot.id !== drag.id), index = others.filter(spot => spot.y < y).length;
    this.layer.setDragged({ id: drag.id, y });
    if (index !== drag.index) { drag.index = index; this.sync(); }
  }
  /** This player's heroes in column order, with a dragged medallion already in the place it would drop into. */
  private columnOrder(): HeroId[] {
    const owned = this.hud.selection.owned, drag = this.reorder?.moved ? this.reorder : null;
    if (!drag) return owned;
    const rest = owned.filter(id => id !== drag.id);
    rest.splice(Math.min(rest.length, drag.index), 0, drag.id);
    return rest;
  }

  /** The world scene's selection callback; a new selection closes the menu. */
  select(ids: HeroId[], mode: SelectionMode) { this.close(); this.hud.selectHeroes(ids, mode); }
  /** Q opens the menu on the unit under the pointer, Esc closes it. Returns true when it used the key. */
  key(event: KeyboardEvent): boolean {
    if (!this.live || this.inventoryOpen || (event.target as HTMLElement).closest?.('input, textarea, select')) return false;
    if (matches(event, 'radial') && !event.repeat && !event.ctrlKey && !event.metaKey) {
      if (this.isOpen) this.close(); else if (this.pointer) this.openAt(this.pointer, null);
      return true;
    }
    if (matches(event, 'cancel') && this.isOpen && !this.hud.armedAbility()) { this.close(); return true; }
    const chart = this.scene()?.mapChart;
    if (matches(event, 'cancel') && chart?.expanded) { chart.toggle(); return true; }
    return false;
  }
  /** The HUD shows while a world is entered (the page's `#hud` is shown); the lobby's backdrop has none. */
  private get live() { return this.shown && this.element('hud')?.hidden === false; }
  /** The inventory dialog is open over the world: the layer's item menu stands over it, and the world's menus close. */
  private get inventoryOpen() { return (this.element('supplies-panel') as HTMLDialogElement | null)?.open === true; }
  /** A page can hide the whole 3D HUD without leaving the world (the scenario viewer's HUD button); orders and keys then fall through to the page. */
  shown = true;
  /** A page element by id, looked up once it exists and kept. */
  private element(id: string) {
    let found = this.elements.get(id);
    if (!found) { found = document.getElementById(id) ?? undefined; if (found) this.elements.set(id, found); }
    return found ?? null;
  }
  /** Links the layer's shaders while the world prepares, so neither its first frames nor the menu's first opening stall. */
  warm = async (renderer: THREE.WebGLRenderer, active: () => boolean) => {
    this.sync();
    // The world's renderer draws the layer under the inventory (and in the filtered mode); its own canvas draws it everywhere else.
    await this.layer.warm(renderer, hudIcons.movement, active);
    const own = this.canvas(renderer);
    // The warm-up draws its pieces onto the canvas, which stays over the page (the lobby's menu too) until something wipes it.
    if (own && !this.glWarmed && active()) { this.glWarmed = true; try { await this.layer.warm(own, hudIcons.movement, active); } finally { own.clear(); } }
  };
  /** Draws the layer; the game scene calls it after each world frame. */
  draw = (renderer: THREE.WebGLRenderer, width: number, height: number, inside = false) => {
    const now = performance.now(), dt = Math.min(.1, (now - this.last) / 1000); this.last = now;
    // While the HUD is up its 3D pieces stand in for the page's top bar and status lines, which the page hides (`hud-3d` class).
    document.body.classList.toggle('hud-3d', this.live);
    if (!this.live) { if (this.isOpen) this.close(); this.closeItem(); this.scene()?.setHudBlocks([]); this.clear(); return; }
    const lifted = this.inventoryOpen;
    if (lifted && this.isOpen) this.close();
    if (!lifted) this.closeItem();
    if (this.pageDirty) {
      this.pageDirty = false;
      this.zoom = Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ui-zoom')) || 1;
      // A page may keep the left edge for its own panel (`--hud-inset`); the party column and the panels stand beside it.
      this.inset = Number.parseFloat(getComputedStyle(this.host).getPropertyValue('--hud-inset')) || 0;
    }
    this.layer.setZoom(this.zoom); this.layer.setInset(this.inset);
    const world = this.scene();
    if (world) world.formationShape = this.hud.formationShape();
    this.layer.setDock(this.dockButtons());
    this.layer.setCorner(this.cornerButtons());
    this.layer.setClockRail([{ id: 'rest', icon: hudIcons.clock, pressed: this.hud.panels().rest }]);
    // The rest panel stands beside the clock, wherever and however large it is drawn.
    const span = Math.round(this.layer.clockSpan);
    if (span !== this.clockSpan) { this.clockSpan = span; document.documentElement.style.setProperty('--clock-span', `${span}px`); }
    const shown = (id: string) => { const element = this.element(id); return element && !element.hidden ? element.textContent?.trim() || null : null; };
    this.layer.setBanners(shown('combat-status'), shown('paused-banner'));
    this.layer.setPaused(this.hud.snapshot ? this.hud.snapshot.paused : null);
    this.layer.setDockColumn(this.weapons ? 'weapon' : this.formations ? 'formation' : null, this.weapons ? this.weaponButtons() : this.formations ? this.formationButtons() : []);
    const scene = this.scene(), wheel = this.pingWheel ?? this.ground;
    const anchor = this.item ? this.item.anchor : wheel ? wheel.anchor() : this.target ? scene?.screenPoint(this.target, 1) ?? null : null;
    this.layer.setMenu(anchor ? this.item ? this.itemNodes() : this.pingWheel ? this.pingNodes(this.pingWheel) : this.ground ? this.groundNodes(this.ground) : this.target ? this.nodes(this.target) : null : null, anchor);
    const focus = wheel ? wheel.targetId ?? '' : this.target;
    scene?.setMenuFocus(focus !== null && anchor ? { id: focus, ...anchor, radius: this.layer.menuRadius } : null);
    // A power armed with its first target outlines that target and every foe that could be its second.
    const armed = this.hud.armedAbility(), chosen = this.hud.armedTargetIds();
    scene?.setTargetHints(armed && (armed.maxTargets ?? 1) > 1 && chosen.length === 1 ? [chosen[0], ...this.secondTargets(chosen[0])] : []);
    if (this.hud.snapshot) { this.layer.setClock(this.hud.snapshot.clock); this.layer.setCombat(this.hud.snapshot.inCombat); }
    const chart = scene?.mapChart;
    this.layer.setChart(chart?.visible ? { canvas: chart.canvas, revision: chart.revision, expanded: chart.expanded, location: chart.location, focus: chart.cameraFocus } : null);
    this.layer.hoverUnit(scene?.hoveredUnitId ?? null);
    this.layer.update(dt);
    scene?.setHudBlocks(this.layer.blocks());
    const own = inside ? null : this.canvas(renderer);
    if (!own) { this.clear(); this.layer.render(renderer, width, height); return; }
    if (lifted) this.layer.render(renderer, width, height, 'rest');
    this.paint(own, renderer, width, height, lifted);
  };

  /** The layer's own renderer, made on first use with the world's pixel ratio; null where WebGL refuses a second context. */
  private canvas(world: THREE.WebGLRenderer) {
    if (!this.gl && !this.glFailed) {
      try {
        const gl = this.gl = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        gl.setClearColor(0x000000, 0); retainShaderPrograms(gl);
        gl.domElement.className = 'hud-layer'; gl.domElement.setAttribute('aria-hidden', 'true');
      } catch (error) { this.glFailed = true; console.warn('The HUD could not have a canvas of its own; it draws into the world\'s.', error); }
    }
    if (this.gl && this.gl.getPixelRatio() !== world.getPixelRatio()) this.gl.setPixelRatio(world.getPixelRatio());
    return this.gl;
  }
  /** Draws the layer on its own canvas: over the page in the stage, or lifted over the inventory with the item menu alone. */
  private paint(gl: THREE.WebGLRenderer, world: THREE.WebGLRenderer, width: number, height: number, lifted: boolean) {
    const canvas = gl.domElement;
    if (lifted) {
      // The popover stands where the stage is, so the layer's pixels land where they would there; shown after the dialog, it is over it.
      const box = this.host.getBoundingClientRect();
      Object.assign(this.lift.style, { left: `${box.left}px`, top: `${box.top}px`, width: `${box.width}px`, height: `${box.height}px` });
      if (!this.lift.isConnected) document.body.append(this.lift);
      if (canvas.parentElement !== this.lift) this.lift.append(canvas);
      if (!this.lift.matches(':popover-open')) this.lift.showPopover();
    } else this.lower(world);
    if (gl.getSize(SIZE).x !== width || SIZE.y !== height) gl.setSize(width, height, false);
    gl.clear(); this.painted = true;
    this.layer.render(gl, width, height, lifted ? 'menu' : 'all');
  }
  /** Puts the canvas back over the world, just after the world's own. */
  private lower(world?: THREE.WebGLRenderer) {
    if (this.lift.isConnected && this.lift.matches(':popover-open')) this.lift.hidePopover();
    const canvas = this.gl?.domElement;
    if (!canvas || canvas.parentElement === this.host) return;
    if (world?.domElement.parentElement === this.host) world.domElement.after(canvas); else this.host.append(canvas);
  }
  /** Wipes the layer's own canvas (the world was left, or the layer draws into the world's frame). */
  clear = () => {
    if (this.gl && this.painted) { this.gl.clear(); this.painted = false; }
    this.lower();
  };

  /** Opens an item's actions round its slot in the open inventory, sending the chosen order. */
  private openItemMenu(actions: ItemAction[], around: HTMLElement, choose: (order: ItemOrder) => void) {
    if (!actions.length) { this.closeItem(); return; }
    const box = this.host.getBoundingClientRect(), slot = around.getBoundingClientRect();
    this.close(); this.layer.resetMenu(); pointTooltip(null);
    this.item = { actions, choose, anchor: { x: slot.left + slot.width / 2 - box.left, y: slot.top + slot.height / 2 - box.top } };
  }
  private closeItem() {
    if (!this.item) return;
    this.item = null; this.hovered = null; this.layer.hover(null); pointTooltip(null);
    const dialog = this.element('supplies-panel'); if (dialog) delete dialog.dataset.hudCursor;
  }
  /** The item menu's entries: each action, a group opening its choices (a hero to heal or hand it to). */
  private itemNodes(): RadialNode[] {
    this.entries.clear(); this.texts.clear();
    const node = (action: ItemAction): RadialNode => {
      if (action.order) this.entries.set(action.id, { kind: 'item', order: action.order });
      this.texts.set(action.id, action.label + (action.disabled ? `\nUnavailable: ${action.disabled}` : ''));
      return { id: action.id, icon: hudIcons[action.glyph], tint: action.tint ?? GILT, disabled: !!action.disabled, ...action.children ? { children: action.children.map(node) } : {} };
    };
    return this.item?.actions.map(node) ?? [];
  }

  /** Opens the menu on a unit (null closes it); returns whether it opened. For scripts and checks as well as the pointer. */
  open(target: string | null) {
    // Every opening starts fresh: no group left open from another unit.
    this.layer.resetMenu(); this.weapons = false; this.formations = false; this.pingWheel = null; this.ground = null;
    this.target = target && this.nodes(target).length ? target : null;
    if (!this.target) this.scene()?.setMenuFocus(null);
    return !!this.target;
  }
  /** The unit the menu is open on (`weapon` for the weapon column), and the ids of the entries it shows. */
  menu() {
    const flat = (nodes: RadialNode[]): string[] => nodes.flatMap(node => [node.id, ...flat(node.children ?? [])]);
    if (this.item) return { target: 'item', entries: flat(this.itemNodes()) };
    if (this.weapons) return { target: 'weapon', entries: this.weaponButtons().map(button => button.id) };
    if (this.formations) return { target: 'formation', entries: this.formationButtons().map(button => button.id) };
    if (this.pingWheel || this.ground) return { target: this.pingWheel ? 'ping' : 'ground', entries: flat(this.pingWheel ? this.pingNodes(this.pingWheel) : this.groundNodes(this.ground!)) };
    return this.target ? { target: this.target, entries: flat(this.nodes(this.target)) } : null;
  }
  /** Where each 3D piece stands on screen and its gap to each edge. For scripts and checks. */
  bounds() { return this.layer.bounds(); }
  /** The hero whose portrait is responding to hover, from the world or the HUD. For scripts and checks. */
  hoveredHero() { return this.layer.hoveredHeroId; }
  /** A medallion's tooltip text. For scripts and checks. */
  heroText(id: string) { return this.tooltip({ kind: 'medallion', id }); }
  /** Presses a bar button (or a weapon in its column, `weapon:<id>`), as a click would. For scripts and checks. */
  pressDock(id: string) { this.press({ kind: 'dock', id }, false); }
  private get isOpen() { return !!this.target || this.weapons || this.formations || !!this.pingWheel || !!this.ground; }
  private close() { this.target = null; this.weapons = false; this.formations = false; this.pingWheel = null; this.ground = null; this.scene()?.setMenuFocus(null); pointTooltip(null); }

  private press(hit: HudHit, shift: boolean) {
    // A stance notch on one of your medallions switches that stance, as its button in the menu would; on another player's it is the medallion.
    if (hit.kind === 'stance' && this.hud.selection.owned.includes(hit.id)) { this.hud.toggleStance(hit.id, hit.stance); return; }
    if (hit.kind === 'medallion' || hit.kind === 'stance') {
      // A second click on the same medallion soon after snaps the camera to that hero.
      const now = performance.now(), twice = this.lastMedallion?.id === hit.id && now - this.lastMedallion.at < DOUBLE_CLICK;
      this.lastMedallion = twice ? null : { id: hit.id, at: now };
      if (twice) this.scene()?.focusUnit(hit.id);
      this.select([hit.id], shift ? 'toggle' : 'replace'); return;
    }
    if (hit.kind === 'dock') { this.dock(hit.id); return; }
    if (hit.kind === 'chart') { this.chart(hit); return; }
    const entry = this.entries.get(hit.id);
    // A greyed entry does nothing; a refused power still has its hero say why (no action left, not ready yet).
    if (this.layer.menuNode(hit.id)?.disabled) { if (entry && 'view' in entry) this.hud.refuse(entry.view); return; }
    if (!entry) { this.layer.chooseGroup(hit.id); return; }
    // A chosen entry flashes and pulses, so the choice is seen even when the menu folds away at once.
    this.layer.confirm(hit.id);
    const wheel = this.pingWheel ?? this.ground;
    if (entry.kind === 'item') { const choose = this.item?.choose; this.closeItem(); choose?.(entry.order); return; }
    if (entry.kind === 'ping') { if (wheel) this.hud.ping(entry.ping, wheel.at, wheel.targetId); this.close(); return; }
    if (entry.kind === 'goto') { if (wheel) this.hud.move(wheel.at); this.close(); return; }
    if (entry.kind === 'halt') { this.hud.stop(); this.close(); return; }
    if (entry.kind === 'follow') { this.hud.follow(this.target!); this.close(); return; }
    if (entry.kind === 'point') { if (wheel) this.hud.emote('point', this.actors(), wheel.at); this.close(); return; }
    if (entry.kind === 'emote') { this.hud.emote(entry.emote, this.actors()); this.close(); return; }
    if (entry.kind === 'greet') { this.hud.emote(entry.emote, [entry.walker], undefined, this.target!); this.close(); return; }
    // The Autocast group's entries flip the setting and leave the menu open to flip more.
    if (entry.kind === 'autocast') { this.hud.toggleAutocast(entry.view); return; }
    const target = this.target!;
    if (entry.kind === 'attack') this.hud.attackWith([entry.heroId], target);
    else if (entry.kind === 'behavior') this.hud.openBehaviorFor(entry.heroId);
    else if (entry.kind === 'pack') this.hud.openPackFor(entry.heroId);
    else if (entry.kind === 'stop') this.hud.stopHeroes([entry.heroId]);
    else if (entry.kind === 'pair') this.hud.armWith(entry.view, target);
    else if (entry.kind !== 'ability') return;
    // On the hero itself a power is used as the bar would (arming it when it needs a target); elsewhere the target is the unit.
    else if (this.isActor(target) && entry.view.heroId === target) this.hud.activate(entry.view);
    else this.hud.useOn(entry.view, target);
    this.close();
  }

  private dock(id: string) {
    this.layer.flashDock(id);
    // A corner button presses the page's own button, so its behavior stays the page's.
    if (id.startsWith('top:')) { document.getElementById(id.slice('top:'.length))?.click(); return; }
    if (id.startsWith('weapon:')) { this.hud.chooseWeapon(id.slice('weapon:'.length)); this.close(); return; }
    if (id.startsWith('formation:')) { this.hud.chooseFormation(id.slice('formation:'.length) as FormationShape); this.close(); return; }
    if (id === 'formation') { const open = this.formations; this.close(); if (!open) this.formations = true; return; }
    if (id === 'weapon') { const open = this.weapons; this.close(); if (!open && this.actors().length) this.weapons = true; return; }
    this.close();
    if (id === 'behavior') this.hud.toggleBehavior();
    else if (id === 'pack') this.hud.toggleSupplies();
    else if (id === 'rest') this.hud.toggleRest();
    else if (id === 'stop') this.hud.stop();
    else if (id === 'pause') this.hud.pause();
  }

  /** A click on the chart looks at the landmark or ground under it; its buttons find the party and unfold or fold it. */
  private chart(hit: Extract<HudHit, { kind: 'chart' }>) {
    const chart = this.scene()?.mapChart;
    if (!chart) return;
    this.close();
    if (hit.id === 'face' && hit.u !== undefined && hit.v !== undefined) chart.lookAt(hit.u, hit.v);
    else if (hit.id === 'chart:follow') { this.layer.flashChart(hit.id); chart.follow(); }
    else if (hit.id === 'chart:expand') { this.layer.flashChart(hit.id); chart.toggle(); }
  }

  /** The page's top bar buttons (the game's settings, sound and developer tools; none in the scenario viewer), drawn as the corner rail. */
  private cornerButtons(): DockButton[] {
    // The bar may mount after this layer (and never exists in the scenario viewer): look for it now and then until found.
    const now = performance.now();
    if (!this.topbar && now - this.topbarAt > 500) {
      this.topbarAt = now; this.topbar = document.querySelector('.topbar');
      if (this.topbar) { this.topbarWatch.observe(this.topbar, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden', 'aria-pressed', 'id'] }); this.cornerDirty = true; }
    }
    if (this.cornerDirty) {
      this.cornerDirty = false;
      this.corner = [...document.querySelectorAll<HTMLButtonElement>('.topbar button')].filter(button => !button.hidden && button.id).map(button => {
        const muted = button.getAttribute('aria-pressed') === 'true';
        const icon = button.id === 'mute-toggle' ? (muted ? hudIcons.soundOff : hudIcons.soundOn) : button.id === 'settings-open' ? hudIcons.settings : hudIcons.pencil;
        return { id: `top:${button.id}`, icon };
      });
    }
    return this.corner;
  }

  /** The bar's buttons as they stand: open panels held down, Stop and the weapon dim with nobody selected. */
  private dockButtons(): DockButton[] {
    const panels = this.hud.panels(), none = !this.actors().length;
    const chosen = this.hud.weaponChoices().find(choice => choice.chosen);
    return [
      { id: 'weapon', icon: hudIcons[this.weaponIcon(chosen?.id ?? '')], disabled: none, pressed: this.weapons },
      { id: 'formation', icon: formationGlyph(this.hud.formationShape()), pressed: this.formations },
      { id: 'behavior', icon: hudIcons.behavior, pressed: panels.behavior, apart: true },
      { id: 'pack', icon: hudIcons.pack, pressed: panels.pack },
      { id: 'stop', icon: hudIcons.stop, disabled: none, tint: 0xd9867a },
    ];
  }
  private weaponIcon(weaponId: string): HudIcon {
    const attack = this.hud.content?.heroes.flatMap(hero => hero.profile.attacks).find(item => item.id === weaponId);
    if (!attack) return 'strike';
    const own = gearIcon(attack.name);
    return own !== 'pack' && own !== 'gladius' ? own : WEAPON_ICONS[attack.damageType] ?? 'strike';
  }
  /** One button per shape, the party's current one held down. */
  private formationButtons(): DockButton[] {
    return FORMATION_SHAPES.map(shape => {
      this.weaponTexts.set(`formation:${shape}`, FORMATION_NAMES[shape]);
      return { id: `formation:${shape}`, icon: formationGlyph(shape), pressed: this.hud.formationShape() === shape };
    });
  }
  /** One button per weapon the selection shares, then Automatic at the top; the chosen one is held down. */
  private weaponButtons(): DockButton[] {
    this.weaponTexts.clear();
    return this.hud.weaponChoices().sort((a, b) => Number(!a.id) - Number(!b.id)).map(choice => {
      const id = `weapon:${choice.id}`;
      this.weaponTexts.set(id, choice.id ? choice.name : 'Automatic weapon\nEach hero picks the best weapon for the target');
      return { id, icon: hudIcons[choice.id ? this.weaponIcon(choice.id) : 'autocast'], pressed: choice.chosen };
    });
  }

  /** This player's heroes in the selection: the ones the menu acts for. */
  private actors(): HeroId[] { return this.hud.selection.ids.filter(id => this.hud.selection.owned.includes(id)); }
  private isActor(id: string) { return this.actors().includes(id); }

  private sync() {
    const snapshot = this.hud.snapshot, content = this.hud.content;
    if (!snapshot || !content) return;
    const owned = this.columnOrder(), selected = this.hud.selection.ids;
    const party: MedallionState[] = content.heroes.filter(hero => snapshot.heroes.some(state => state.id === hero.id))
      .sort((a, b) => (owned.includes(a.id) ? owned.indexOf(a.id) : owned.length) - (owned.includes(b.id) ? owned.indexOf(b.id) : owned.length))
      .map(definition => {
        const state = snapshot.heroes.find(hero => hero.id === definition.id)!;
        return { id: definition.id, icon: this.heroIcon(definition.id), tint: definition.color,
          portrait: portraitUrl(definition.portrait), fallen: state.health.dead, dying: state.health.dying, away: !state.connected,
          hp: state.health.hp / state.health.maxHp, ward: state.health.temporaryHp / Math.max(1, state.health.maxHp), actions: state.combat.actions, slots: snapshot.inCombat && !state.health.dying ? actionSlots(state) : undefined, wounds: state.health.wounds, maxWounds: state.health.maxWounds, ...state.class.summons ? { summons: state.class.summons } : {}, ...state.class.fury ? { fury: state.class.fury } : {}, ...state.class.judgment ? { judgment: state.class.judgment } : {}, selected: selected.includes(definition.id), own: owned.includes(definition.id),
          stances: { move: state.combat.running ? 'run' : state.combat.hiding ? 'hide' : null, anticipating: state.combat.anticipating, hidden: !!state.hidden },
          queue: !state.connected ? [hudIcons.clock] : state.queue?.map(order => hudIcons[order.kind === 'move' ? 'movement' : order.kind === 'attack' ? order.attackId ? this.weaponIcon(order.attackId) : 'strike' : abilityIcon(order.abilityId)]) };
      });
    this.layer.setParty(party);
    this.views = new Map(this.hud.abilityViews().map(view => [`${view.heroId}:${view.abilityId}`, view]));
    // A target that died or left, or a selection with nobody left to act, closes the menu.
    if (this.target && !this.nodes(this.target).length || this.weapons && !this.actors().length) this.close();
  }
  private heroIcon(id: string) {
    return hudIcons[classIcon(this.hud.content?.heroes.find(hero => hero.id === id)?.classId ?? '')];
  }

  /** The menu for a target: one hero's actions directly, or several heroes' portraits that each open their actions. */
  private nodes(target: string): RadialNode[] {
    this.entries.clear(); this.texts.clear();
    const snapshot = this.hud.snapshot;
    if (!snapshot) return [];
    const enemy = snapshot.enemies.find(unit => unit.id === target), hero = snapshot.heroes.find(unit => unit.id === target);
    if (enemy?.health.dead || hero?.health.dead || !enemy && !hero) return [];
    const armed = this.hud.armedAbility(), actors = this.actors();
    const name = (heroId: string) => this.hud.content?.heroes.find(item => item.id === heroId)?.name ?? '';
    const power = (view: AbilityView, kind: 'ability' | 'autocast' = 'ability'): RadialNode => {
      const id = `${view.heroId}:${view.abilityId}${kind === 'autocast' ? ':autocast' : ''}`; this.entries.set(id, { kind, view });
      this.texts.set(id, [view.name, `${view.heroName} · ${view.cost}${view.uses}`, view.description,
        ...view.autocast === null ? [] : [`Autocast ${view.autocast ? 'on' : 'off'}`], ...view.disabled ? [`Unavailable: ${view.disabled}`] : []].join('\n'));
      // Autocast glints belong to the Autocast group only; elsewhere the menu is about using the power.
      return { id, icon: hudIcons[view.icon], tint: GILT, autocast: kind === 'autocast' ? view.autocast : undefined, disabled: kind === 'ability' && !!view.disabled,
        armed: kind === 'ability' && (view.active || armed?.heroId === view.heroId && armed.abilityId === view.abilityId) };
    };
    const command = (kind: 'attack' | 'behavior' | 'pack' | 'stop', heroId: HeroId, icon: HudIcon, tint = GILT): RadialNode => {
      const id = `${heroId}:${kind}`; this.entries.set(id, { kind, heroId });
      // An attack needs a hero who is standing and connected; the other commands are always offered.
      const state = kind === 'attack' ? snapshot.heroes.find(unit => unit.id === heroId) : undefined;
      const refused = kind !== 'attack' ? '' : !state || state.health.dead ? 'This hero has fallen.' : !state.connected ? 'This hero is not connected.' : '';
      this.texts.set(id, (kind === 'attack' ? this.attackText(heroId) : `${{ behavior: 'Behavior', pack: 'Pack', stop: 'Stop' }[kind]}\n${name(heroId)}`) + (refused ? `\nUnavailable: ${refused}` : ''));
      return { id, icon: hudIcons[icon], tint, disabled: !!refused };
    };
    // Emotes are talk between players: refused in a fight, which the glyph says. The selected heroes that are free all join in.
    const fighting = snapshot.inCombat ? 'Not in a fight.' : '';
    const emoteNode = (emote: EmoteKind, id: string, label: string): RadialNode => {
      this.entries.set(id, { kind: 'emote', emote }); this.texts.set(id, label + (fighting ? `\nUnavailable: ${fighting}` : ''));
      return { id, icon: hudIcons[emote], tint: GILT, disabled: !!fighting };
    };
    const emoteGroup = (): RadialNode => {
      const id = `${target}:emotes`; this.texts.set(id, 'Emotes\nWave, cheer or bow for the whole room to see');
      return { id, icon: hudIcons.emotes, tint: GILT, children: [emoteNode('wave', `${target}:wave`, 'Wave'), emoteNode('cheer', `${target}:cheer`, 'Cheer'), emoteNode('bow', `${target}:bow`, 'Bow')] };
    };
    const viewsOf = (heroId: HeroId) => [...this.views.values()].filter(view => view.heroId === heroId);
    // A hero's powers in their categories (Spells, Powers, Tactics), each a group; a category of one is that entry alone.
    const grouped = (heroId: HeroId, items: { view: AbilityView; node: RadialNode }[]): RadialNode[] => CATEGORIES.flatMap(([key, label, glyph]) => {
      const nodes = items.filter(item => category(item.view) === key).map(item => item.node);
      if (nodes.length < 2) return nodes;
      const id = `${heroId}:${key}`; this.texts.set(id, label);
      return [{ id, icon: hudIcons[glyph], tint: GILT, children: nodes }];
    });
    // On one of the acting heroes: the powers used on oneself by category, then the hero's own group (behavior, pack and an Autocast
    // group whose entries flip each power's autocast), the emotes and Stop.
    if (hero && actors.includes(target)) {
      const autocasts = viewsOf(target).filter(view => view.autocast !== null).map(view => power(view, 'autocast'));
      this.texts.set(`${target}:autocasts`, 'Autocast\nWhich powers the AI may use by itself');
      this.texts.set(`${target}:hero`, name(target));
      const own = [command('behavior', target, 'behavior'), command('pack', target, 'pack'),
        ...autocasts.length ? [{ id: `${target}:autocasts`, icon: hudIcons.autocast, tint: GILT, children: autocasts }] : []];
      return [...grouped(target, viewsOf(target).filter(view => view.target === 'self').map(view => ({ view, node: power(view) }))),
        { id: `${target}:hero`, icon: this.heroIcon(target), tint: this.hud.content?.heroes.find(item => item.id === target)?.color ?? GILT, children: own },
        emoteGroup(), command('stop', target, 'stop', 0xd9867a)];
    }
    // A power that can take several foes (Razor Wind) asks how: this one alone, or this one and another next to it.
    const targeted = (view: AbilityView): RadialNode => {
      if ((view.maxTargets ?? 1) < 2 || !enemy) return power(view);
      const single = { ...power(view), icon: hudIcons.focus };
      this.texts.set(single.id, `${view.name}: this foe alone\n${this.texts.get(single.id)!.split('\n').slice(1).join('\n')}`);
      const choices = [single];
      if (this.secondTargets(target).length) {
        const id = `${view.heroId}:${view.abilityId}:pair`; this.entries.set(id, { kind: 'pair', view });
        this.texts.set(id, `${view.name}: add a second foe\nPick another foe beside this one; click this one again to cast at it alone.`);
        choices.push({ id, icon: hudIcons.multipleTargets, tint: GILT, disabled: !!view.disabled });
      }
      const group = `${view.heroId}:${view.abilityId}:how`; this.texts.set(group, view.name);
      return { id: group, icon: hudIcons[view.icon], tint: GILT, disabled: !!view.disabled, children: choices };
    };
    const actionsOf = (heroId: HeroId): RadialNode[] => enemy
      ? [command('attack', heroId, 'strike'), ...grouped(heroId, viewsOf(heroId).filter(view => view.target === 'enemy' || view.target === 'creature').map(view => ({ view, node: targeted(view) })))]
      // Breath of Life only reaches the Dying, Lay on Hands anyone, and other ally powers only allies who can act: elsewhere they are not offered at all.
      : grouped(heroId, viewsOf(heroId).filter(view => (view.target === 'ally' || view.target === 'creature' && !hero?.health.dying) && (view.healTarget || !!view.dyingTarget === !!hero?.health.dying)).map(view => ({ view, node: power(view) })));
    const acting = actors.map(heroId => ({ heroId, actions: actionsOf(heroId) })).filter(entry => entry.actions.length);
    // On a living party hero the selection can follow: one entry for all of it, beside the powers.
    const following: RadialNode[] = hero && actors.length && !enemy ? [{ id: `${target}:follow`, icon: hudIcons.movement, tint: GILT }] : [];
    if (following.length) { this.entries.set(following[0].id, { kind: 'follow' }); this.texts.set(following[0].id, `Follow\n${name(target)}`); }
    // Beside it, a handshake or hug: the nearest selected hero walks up to it.
    const walker = hero && !enemy ? actors.filter(id => id !== target).map(id => snapshot.heroes.find(unit => unit.id === id)!).filter(unit => unit && !unit.health.dead)
      .sort((a, b) => Math.hypot(a.x - hero.x, a.z - hero.z) - Math.hypot(b.x - hero.x, b.z - hero.z))[0] : undefined;
    const greetings: RadialNode[] = [];
    if (hero && walker) {
      const group = `${target}:greet`, refused = fighting || (!hero.connected ? `${name(target)} is away.` : hero.health.dying ? `${name(target)} cannot meet anyone now.` : '');
      this.texts.set(group, `Greet\n${name(target)}`);
      const choices = (['handshake', 'hug'] as const).map(emote => {
        const id = `${target}:${emote}`; this.entries.set(id, { kind: 'greet', emote, walker: walker.id }); this.texts.set(id, `${emote === 'hug' ? 'Hug' : 'Handshake'}\n${name(walker.id)} meets ${name(target)}${refused ? `\nUnavailable: ${refused}` : ''}`);
        return { id, icon: hudIcons[emote], tint: GILT, disabled: !!refused };
      });
      greetings.push({ id: group, icon: hudIcons.handshake, tint: GILT, disabled: !!refused, children: choices });
    }
    if (acting.length === 1) return [...acting[0].actions, ...following, ...greetings];
    for (const { heroId } of acting) this.texts.set(`${heroId}:portrait`, name(heroId));
    return [...following, ...greetings, ...acting.map(({ heroId, actions }) => ({ id: `${heroId}:portrait`, icon: this.heroIcon(heroId), tint: this.hud.content?.heroes.find(item => item.id === heroId)?.color ?? GILT, children: actions }))];
  }

  /** What an Alt press landed on: the chart's ground (a landmark's pin snaps to it), a living unit in the world, or the world's ground. */
  private pingTarget(event: { clientX: number; clientY: number }, hit: HudHit | null): PingTarget | null {
    const box = this.host.getBoundingClientRect(), screen = { x: event.clientX - box.left, y: event.clientY - box.top };
    if (hit?.kind === 'chart') {
      const chart = this.scene()?.mapChart;
      if (hit.id !== 'face' || !chart || hit.u === undefined || hit.v === undefined) return null;
      const landmark = chart.landmarkAt(hit.u, hit.v), at = landmark ? { x: landmark.x, z: landmark.z } : chart.worldAt(hit.u, hit.v);
      return { subject: 'ground', at, anchor: () => screen };
    }
    if (hit) return null;
    const scene = this.scene(), snapshot = this.hud.snapshot;
    if (!scene || !snapshot) return null;
    const id = scene.unitAt(event.clientX, event.clientY), enemy = snapshot.enemies.find(unit => unit.id === id), hero = snapshot.heroes.find(unit => unit.id === id);
    const unit = enemy ?? hero;
    if (id && unit && !unit.health.dead) return { subject: enemy ? 'enemy' : 'ally', at: { x: unit.x, z: unit.z }, targetId: id, anchor: () => scene.screenPoint(id, 1) };
    const ground = scene.groundAt(event.clientX, event.clientY);
    if (!ground) return null;
    const at = { x: ground.x, z: ground.z };
    return { subject: 'ground', at, anchor: () => scene.groundScreenPoint(at) };
  }
  /**
   * Opens the menu under a pointer: a unit's actions (or its ping wheel when the selection has none for it), the ground menu on open
   * ground, or the chart's ping wheel on its face.
   */
  private openAt(pointer: { clientX: number; clientY: number }, hit: HudHit | null) {
    const target = this.pingTarget(pointer, hit);
    if (!target) { this.close(); return; }
    if (target.targetId) { if (!this.open(target.targetId)) this.openPingWheel(target); return; }
    if (hit || !this.actors().length) { this.openPingWheel(target); return; }
    this.close(); this.layer.resetMenu(); this.ground = target; pointTooltip(null);
  }
  /** The ground menu: Move here and Stop for the selected heroes, then a group of the ground's ping choices. */
  private groundNodes(ground: PingTarget): RadialNode[] {
    const pings = this.pingNodes({ ...ground, subject: 'ground' });
    this.entries.set('ground:goto', { kind: 'goto' }); this.texts.set('ground:goto', 'Move here');
    this.entries.set('ground:halt', { kind: 'halt' }); this.texts.set('ground:halt', DOCK_TEXTS.stop);
    this.texts.set('ground:pings', 'Ping');
    const fighting = this.hud.snapshot?.inCombat;
    this.entries.set('ground:point', { kind: 'point' }); this.texts.set('ground:point', `Point here${fighting ? '\nUnavailable: Not in a fight.' : ''}`);
    return [{ id: 'ground:goto', icon: hudIcons.movement, tint: GILT }, { id: 'ground:halt', icon: hudIcons.stop, tint: 0xd9867a },
      { id: 'ground:point', icon: hudIcons.point, tint: GILT, disabled: !!fighting },
      { id: 'ground:pings', icon: hudIcons[PING_LOOKS.mark.icon], tint: GILT, children: pings }];
  }
  private openPingWheel(target: PingTarget) { this.close(); this.layer.resetMenu(); this.pingWheel = target; pointTooltip(null); }
  /** The ping wheel: the choices that fit what was pinged, the plain mark first. */
  private pingNodes(wheel: PingTarget): RadialNode[] {
    this.entries.clear(); this.texts.clear();
    return PING_CHOICES[wheel.subject].map(ping => {
      const id = `ping:${ping}`, look = PING_LOOKS[ping]; this.entries.set(id, { kind: 'ping', ping }); this.texts.set(id, look.label);
      return { id, icon: hudIcons[look.icon], tint: look.color };
    });
  }

  /** Living, visible foes close enough to `first` to share a split power with it, as the rules check it. */
  private secondTargets(first: string): string[] {
    const enemies = this.hud.snapshot?.enemies ?? [], anchor = enemies.find(enemy => enemy.id === first);
    if (!anchor) return [];
    // Sizes come from the content (a snapshot may carry only positions).
    const body = (enemy: typeof anchor) => ({ x: enemy.x, z: enemy.z, radius: enemy.radius ?? this.hud.content?.enemies.find(item => item.id === enemy.id)?.radius ?? .5 });
    return enemies.filter(enemy => enemy.id !== first && !enemy.health.dead && !enemy.hidden && surfaceDistance(body(anchor), body(enemy)) <= ADJACENT_FOES + GEOMETRY_EPSILON).map(enemy => enemy.id);
  }

  /** The weapon the hero would strike with and what one hit deals, such as "Longsword · 1d8+2 slashing". */
  private attackText(heroId: HeroId) {
    const definition = this.hud.content?.heroes.find(hero => hero.id === heroId), state = this.hud.snapshot?.heroes.find(hero => hero.id === heroId);
    // An autoattack cantrip replaces the weapon on a plain attack order.
    const cantrip = state?.behavior.autoAttack && this.hud.content?.classes?.find(cls => cls.id === definition?.classId)?.abilities.find(ability => ability.id === state.behavior.autoAttack);
    if (cantrip) return `Attack\n${cantrip.name}`;
    const attacks = definition?.profile.attacks ?? [];
    const attack = attacks.find(item => item.id === state?.behavior.preferredAttackId) ?? attacks[0];
    if (!definition || !attack) return 'Attack';
    const stat = attackStat(attack, definition.profile.attributes), modifier = (stat ? definition.profile.attributes[stat] : 0) + attack.bonus;
    return `Attack\n${attack.name} · ${attack.count}d${attack.sides}${modifier ? (modifier > 0 ? `+${modifier}` : `${modifier}`) : ''} ${attack.damageType}`;
  }

  /** The in-game hour, such as "14:05". */
  private clockText() {
    const minutes = this.hud.snapshot?.clock ?? 0, hour = Math.floor(minutes / 60) % 24, minute = Math.floor(minutes % 60);
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  }

  /** The parchment tooltip for whatever the pointer is over. */
  private tooltip(hit: HudHit): string | null {
    if (hit.kind === 'chart') {
      const chart = this.scene()?.mapChart;
      if (hit.id === 'chart:follow') return 'Find party · F';
      if (hit.id === 'chart:expand') return `${chart?.expanded ? 'Fold' : 'Unfold'} map · M`;
      if (hit.id !== 'face' || hit.u === undefined || hit.v === undefined) return null;
      return chart?.landmarkAt(hit.u, hit.v)?.name.toLocaleLowerCase().replace(/(^|\s)\S/g, letter => letter.toLocaleUpperCase()) ?? null;
    }
    if (hit.kind === 'dock' && hit.id.startsWith('top:')) return document.getElementById(hit.id.slice('top:'.length))?.dataset.tooltip ?? null;
    if (hit.kind === 'dock') return this.weaponTexts.get(hit.id) ?? (hit.id === 'formation' ? `Formation\n${FORMATION_NAMES[this.hud.formationShape()]}` : hit.id === 'weapon' ? `Weapon\n${this.hud.weaponChoices().find(choice => choice.chosen)?.name ?? 'Automatic weapon'}`
      : hit.id === 'pause' ? `${this.clockText()}\n${this.hud.snapshot?.paused ? 'Paused · Space' : 'Pause · Space'}` : DOCK_TEXTS[hit.id] ?? null);
    if (hit.kind === 'stance') return hit.stance === 'hide' ? `Hide\n${this.hud.snapshot?.heroes.find(hero => hero.id === hit.id)?.hidden ? 'Hidden' : 'Sneaking'}` : hit.stance === 'run' ? 'Run · R' : 'Anticipate Danger';
    if (hit.kind === 'socket') return this.texts.get(hit.id) ?? ({ spells: 'Spells', powers: 'Powers', tactics: 'Tactics' } as Record<string, string>)[hit.id] ?? null;
    const { snapshot, content } = this.hud;
    return snapshot && content ? heroSummary(snapshot, content, hit.id, this.hud.selection.owned) : null;
  }

  dispose() {
    this.detach(); delete this.host.dataset.hudCursor; this.layer.dispose();
    this.lift.remove(); if (this.gl) { this.gl.dispose(); this.gl.forceContextLoss(); this.gl.domElement.remove(); }
  }
}
